
import { defineComponent, computed, onMounted, ref, Ref } from 'vue';
import { useGettext } from '@jshmrtn/vue3-gettext';
import useSilentLogin from '@/composables/useSilentLogin';
import useNavigateTo from '@/composables/useNavigateTo';
import { CxServiceUnavailableError } from '@/classes/error.class';
import CxSpinner from '@/components/CxSpinner.vue';
import resetApi from '@/http/reset.api';
import { ErrorType, useDisplayError } from '@/composables/useDisplayError';

export default defineComponent({
  name: 'CxRecovery',
  components: { CxSpinner },
  setup() {
    // Use composables and plugins
    const { isSilentLoginInProgress, isSilentRedirectInProgress, silentLogin } = useSilentLogin();
    const { navigateToLogin } = useNavigateTo();
    const language = useGettext();
    const { currentErrorType, errorText } = useDisplayError(language);

    // Data
    const username: Ref<string> = ref('');
    const shouldDisplaySuccess: Ref<boolean> = ref(false);
    const isRecoveryQueryInProgress: Ref<boolean> = ref(false);

    // Computed
    const translations = computed((): {
        recovery: string;
        username: string;
        reset: string;
        back: string;
        headline: string;
        note: string;
        recoveryEmailSent: string;
        userDoesNotExist: string;
        serviceUnavailable: string;
        unexpectedError: string;
        } => {
      return {
        recovery: language.$gettext('Password Recovery'),
        username: language.$gettext('Username:'),
        reset: language.$gettext('Reset password'),
        back: language.$gettext('Back to login'),
        headline: language.$gettext('Dear Customers'),
        note: language.$gettext('Please note that if you are retrieving your updated username and password reset link, you will need to use the email address attached to your account'),
        recoveryEmailSent: language.$gettext('An email containing password reset instructions has been sent to you. Use the link in it to set a new password.'),
        userDoesNotExist: language.$gettext('Sorry, this user doesn\'t exist.'),
        serviceUnavailable: language.$gettext('Sorry, service is unavailable - please try again later'),
        unexpectedError: language.$gettext('Unexpected error has occurred - please try again later or contact support team'),
      };
    });

    // Methods
    const onSilentLoginError = () => {
      currentErrorType.value = ErrorType.ServiceUnavailable;
    };
    const recoverPassword = async (event: Event): Promise<void> => {
      if (event) event.preventDefault();
      if (!username.value) return;
      try {
        shouldDisplaySuccess.value = false;
        isRecoveryQueryInProgress.value = true;
        const result = await resetApi.recoverPassword(username.value);
        if (!result) currentErrorType.value = ErrorType.UserDoesNotExist;
        else shouldDisplaySuccess.value = true;
      } catch (error) {
        if (error instanceof CxServiceUnavailableError) currentErrorType.value = ErrorType.ServiceUnavailable;
        else currentErrorType.value = ErrorType.UnexpectedError;
      } finally {
        isRecoveryQueryInProgress.value = false;
      }
    };

    // Lifecycle Hooks
    onMounted(() => silentLogin(onSilentLoginError));

    // Expose properties and methods
    return {
      // Data
      username,
      shouldDisplaySuccess,
      isSilentLoginInProgress,
      isSilentRedirectInProgress,
      isRecoveryQueryInProgress,
      currentErrorType,
      // Computed
      translations,
      errorText,
      // Methods
      recoverPassword,
      navigateToLogin
    };
  }
});
